<template>
  <div class="vp-image-input my-4 lg:my-0 lg:flex-none lg:w-1/6">
    <HiddenInput :input-id="inputId" />
    <Accessible>
      <label :for="uid">Add image</label>
    </Accessible>
    <input
      :id="uid"
      ref="imgInput"
      type="file"
      class="hidden"
      multiple
      accept="image/*"
      @change="onImageSelected"
    >
    <div
      class="vp-file-upload"
      title="Change logo"
      @click="addImage"
    >
      <img
        v-if="imgUrl"
        class="w-100 h-100 object-cover"
        :src="imgUrl"
        alt="Venue logo"
      >
      <div
        class="bg-vp-primary absolute h-full w-full top-0 left-full opacity-80 transition-all"
        :style="`transform: translateX(${-100.0 + uploadProgress}%)`"
      />
      <div
        class="bg-vp-gray-light-2 absolute h-full w-full top-0 left-0 transition-all flex justify-center items-center"
        :class="{ 'opacity-80': showProcessingOverlay, 'opacity-0': !showProcessingOverlay }"
      >
        Processing...
      </div>
      <div
        type="button"
        class="image-select-overlay"
        :class="{ 'opacity-80': !imgUrl, 'opacity-0': !!imgUrl }"
      >
        <Image class="text-4xl" />
        <span>Add logo</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Image } from 'mdue';
import { ImageSource, openDefaultEditor } from '@pqina/pintura';
import { computed, inject, ref, watch, getCurrentInstance } from 'vue';
import { useAttachmentStore } from '@/stores/attachment';
import { HiddenInput, useFormsStoreComposable } from 'vuniform';
import { AttachmentStatus } from '@/types/Attachment';
import Accessible from '@/components/Accessible.vue';
type Props = {
  inputId: string;
  previewFieldId?: string;
};

const uid = `vp-image-input-${getCurrentInstance()?.uid}`;
const props = defineProps<Props>();

const formId = inject('formId', '');
const { formsStore, fieldGetValue } = useFormsStoreComposable(formId);

const imgInput = ref<HTMLInputElement | undefined>();
const attachmentStore = useAttachmentStore();

if (props.previewFieldId) {
  formsStore.INIT_FORM_FIELD({ formId, name: props.previewFieldId, config: {} });
}

const imgAttachmentKey = ref<string | undefined>();

const imgPendingAttachment = computed(() =>
  imgAttachmentKey.value ? attachmentStore.pendingAttachments[imgAttachmentKey.value] : undefined,
);
const imgAttachment = computed(() => imgPendingAttachment.value?.attachment);
const attachmentStatus = computed(() => imgAttachment.value?.status);

const imgUrl = computed(() => {
  if (imgPendingAttachment.value?.dataLoaded) {
    return imgPendingAttachment.value.previewData;
  }

  return props.previewFieldId ? fieldGetValue(props.previewFieldId) : '';
});

const uploadProgress = computed(() => imgPendingAttachment.value?.uploadProgress || 100);

const showProcessingOverlay = computed(() => {
  return attachmentStatus.value == AttachmentStatus.UPLOADING;
});

watch(attachmentStatus, () => {
  if (!imgAttachment.value?.id) {
    return;
  }

  formsStore.STAGE_FIELD_CHANGE({
    query: { formId, field: props.inputId },
    value: imgAttachment.value?.id,
  });
});

const addImage = () => {
  imgInput.value?.click();
};

const onImageSelected = async () => {
  const files = imgInput.value?.files;
  if (!files || files.length === 0) {
    return;
  }
  await openEditor(files[0]);
};

const openEditor = async (imgSrc: ImageSource | string) => {
  const editor = openDefaultEditor({
    src: imgSrc,
    cropSelectPresetOptions: [
      [1, 'Square'],
      [4 / 3, 'Landscape 4/3'],
      [3 / 4, 'Portrait 3/4'],
      [16 / 9, 'Portrait 16/9'],
    ],
  });

  // Fires when we're done editing the image
  editor.on('process', async ({ dest: file }) => {
    const [key] = attachmentStore.saveFiles(convertToFilesList([file]));
    imgAttachmentKey.value = key;
  });
};

const convertToFilesList = (files: any[]): FileList => {
  const dataTransfer = new DataTransfer();
  files.forEach((file: any) => {
    dataTransfer.items.add(new File([file], file.name));
  });
  return dataTransfer.files;
};
</script>
